import { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'

import api from 'lib/api'
import { formatDate } from 'utils/date'
import { useStore } from 'lib/store'
import { useToast } from 'hooks/useToast'
import { Button, Drawer, Icon, Input } from 'components'
import { FormData, defaultValues, schema } from './utils'
import { FileUploadCurriculum } from './file-upload-curriculum'
import { maskCPF, maskDate, maskOnlyNumbers, maskPhone } from 'utils/mask'

interface Props {
  vacancyId: number
  vacancyTitle: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
  onReviewCurriculum: () => void
}

export const DrawerPreCandidature = ({
  vacancyId,
  vacancyTitle,
  open,
  onClose,
  onSuccess,
  onReviewCurriculum
}: Props) => {
  const toast = useToast()
  const { user, updateStoreUser } = useStore(store => store)
  const [loading, setLoading] = useState(false)
  const [curriculumData, setCurriculumData] = useState<Record<
    string,
    unknown
  > | null>(null)

  const formMethods = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues
  })

  useEffect(() => {
    if (user) {
      formMethods.reset({
        name: user.name || '',
        email: user.email || '',
        cpf: maskCPF(user.cpf) || '',
        birthDate: formatDate(user.birthday, 'dd/MM/yyyy'),
        phone: maskPhone(user.phone),
        linkedin: user.linkedin
      })
    }
  }, [user])

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true)

      const [birthdayDay, birthdayMonth, birthdayYear] =
        data.birthDate.split('/')

      await api(
        '/applicants/pre-apply',
        {
          method: 'POST',
          data: {
            ...curriculumData,
            ...data,
            vacancyId,
            cpf: maskOnlyNumbers(data.cpf) || '',
            phone: maskOnlyNumbers(data.phone),
            birthDate: `${birthdayYear}-${birthdayMonth}-${birthdayDay}`,
            contacts: {
              email: data.email,
              linkedin: data?.linkedin || '',
              phone: maskOnlyNumbers(data.phone)
            }
          }
        },
        'applicants'
      )

      updateStoreUser({
        name: data.name,
        email: data.email,
        cpf: maskOnlyNumbers(data.cpf) || '',
        birthday: `${birthdayYear}-${birthdayMonth}-${birthdayDay}`,
        phone: maskOnlyNumbers(data.phone) || '',
        linkedin: data.linkedin ?? ''
      })

      onSuccess()
    } catch (error) {
      console.error('Error submitting pre-application:', error)
      toast.error({
        title:
          'Ocorreu um erro ao enviar sua candidatura. Por favor, tente novamente.'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Drawer open={open} onOpenChange={onClose}>
      <FormProvider {...formMethods}>
        <div className="flex flex-col gap-4 min-h-[80dvh] w-full">
          <div className="space-y-1">
            <span className="bg-purple-100 text-purple-700 text-[12px] pl-2 pr-2.5 py-1 rounded-full">
              Pré-candidatura
            </span>
            <h2 className="text-sm font-semibold">{vacancyTitle}</h2>
          </div>

          <form
            className="flex flex-col gap-2 flex-1"
            onSubmit={formMethods.handleSubmit(onSubmit)}
          >
            <FileUploadCurriculum
              onUploadSuccess={data => setCurriculumData(data)}
              onUploadError={() => setCurriculumData(null)}
            />

            <div className="flex flex-col mt-2">
              <h3 className="font-bold text-body text-gray-800">
                Dados obrigatórios para a candidatura
              </h3>

              <p className="text-gray-600 text-body2">
                Para continuar sua candidatura, preencha os campos básicos para
                efetuar a candidatura. Essa vaga também pode ter outros campos
                obrigatórios na próxima etapa.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <Input
                required
                name="name"
                label="Nome"
                placeholder="Digite seu nome"
              />
              <Input
                required
                name="birthDate"
                label="Data de nascimento"
                placeholder="DD/MM/AAAA"
                mask={maskDate}
                maxLength={10}
              />
              <Input
                required
                name="email"
                label="E-mail"
                placeholder="Digite seu e-mail"
              />
              <Input
                required
                name="cpf"
                label="CPF"
                placeholder="Digite seu CPF"
                mask={maskCPF}
                maxLength={14}
                inputMode="decimal"
              />
              <Input
                required
                name="phone"
                label="Celular"
                placeholder="Digite seu celular com DDD"
                mask={maskPhone}
                maxLength={15}
                inputMode="decimal"
              />
              <Input
                name="linkedin"
                label="Linkedin"
                placeholder="Ex: linkedin.com/in/SeuLinkedin"
              />
            </div>

            <div className="w-full flex flex-col sm:flex-row gap-y-2 gap-x-4 mt-auto [&>button]:py-3 [&>button]:w-full">
              <Button
                type="button"
                onClick={onReviewCurriculum}
                className="items-center gap-2 bg-transparent text-primary !border !border-primary-700/50"
              >
                <Icon face="edit" size="md" />
                Candidatura completa
              </Button>

              <Button
                type="submit"
                colorCompany
                loading={loading && 'Enviando...'}
                className="items-center gap-2"
              >
                <Icon face="inventory" size="md" />
                Enviar candidatura
              </Button>
            </div>
          </form>
        </div>
      </FormProvider>
    </Drawer>
  )
}
